import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, MessageCircle } from "lucide-react";
import { CONTACT } from "../lib/data";
import { trackContact } from "../lib/fb";

const Line = ({ text, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {text}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen border-b border-black overflow-hidden bg-[#0E0E0E] flex flex-col"
    >
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0" aria-hidden="true">
        <img
          src="/images/hero_bg.jpg"
          alt=""
          data-testid="hero-bg-image"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/25" aria-hidden="true" />
      <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-black/90 to-transparent" aria-hidden="true" />

      <div className="relative z-10 flex-1 flex flex-col justify-end px-6 md:px-12 lg:px-24 pt-32 pb-16 md:pb-24 text-[#F4F4F0]">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="flex items-center gap-3 mb-6"
          data-testid="hero-overline"
        >
          <span className="flex gap-1">
            <span className="w-2.5 h-2.5 rounded-full bg-[#00AEEF]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#EC008C]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#FFD200]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#F4F4F0]" />
          </span>
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#F4F4F0]/70">
            Offset Printing — Press Complex, Indore
          </p>
        </motion.div>

        <motion.h1
          style={{ y: textY }}
          data-testid="hero-heading"
          className="font-display font-bold uppercase tracking-tighter leading-[0.85] text-[15vw] xl:text-[10rem] 2xl:text-[11.5rem] drop-shadow-[0_4px_24px_rgba(0,0,0,0.6)]"
        >
          <Line text="SIDDHARTH" delay={0.35} />
          <span className="text-[#FF3B30]">
            <Line text="OFFSETS" delay={0.5} />
          </span>
        </motion.h1>

        <div className="mt-10 md:mt-14 flex flex-col md:flex-row md:items-end gap-8 md:gap-16">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="font-mono text-sm md:text-base leading-relaxed max-w-md text-[#F4F4F0]/85"
          >
            High-volume offset printing with obsessive colour accuracy. Brochures, catalogs, packaging, labels, magazines & books — printed, finished and delivered from Indore.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            className="flex flex-wrap gap-4"
          >
            <a
              href={CONTACT.whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-btn"
              onClick={trackContact}
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest bg-[#25D366] text-black border border-black px-6 py-4 hover:bg-black hover:text-[#25D366] hover:border-[#25D366] transition-colors duration-200"
            >
              <MessageCircle size={14} /> Connect on WhatsApp
            </a>
            <a
              href="#contact"
              data-testid="hero-quote-btn"
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest bg-[#F4F4F0] text-black px-6 py-4 hover:bg-[#FF3B30] hover:text-[#F4F4F0] transition-colors duration-200"
            >
              Get a quote <ArrowDownRight size={14} />
            </a>
          </motion.div>
        </div>
      </div>

      <div className="relative z-10 flex justify-between items-center px-6 md:px-12 lg:px-24 pb-6 font-mono text-[10px] uppercase tracking-[0.3em] text-[#F4F4F0]/50">
        <span>Scroll — CMYK / 300DPI</span>
        <span className="hidden md:inline">EST. INDORE</span>
      </div>
    </section>
  );
};
